import { useEffect, useMemo, useState } from 'react';
import { Stack, Button, Group } from '@repletoryreach/sdk-ui';
import { useDebounce } from '@repletoryreach/sdk-utils/hooks';
import {
  Alert,
  EmptyState,
  KpiFilterRow,
  PageHeader,
  TableShell,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { useUsers } from '../../data/useUsers';
import { useTenantMap } from '../../data/useTenants';
import { UsersTable } from './components/UsersTable';
import { UsersGrid } from './components/UsersGrid';
import { UsersList } from './components/UsersList';
import { FilterDrawer, type FilterField, type FilterValues } from '@repletoryreach/sdk-ui/data-display';
import { usePersistedState } from '../../lib/usePersistedState';
import { ActiveFilterPills, type ActiveFilterPill } from '../../components/ActiveFilterPills';
import { TableFooter } from '../../components/TableFooter';
import { useT } from '../../i18n/I18nProvider';

type KpiKey = 'all' | 'tenant' | 'platform' | 'recent';

/** The server's default row cap. It reports the real total alongside the page. */
const SERVER_ROW_CAP = 500;

export default function UsersPage() {
  const t = useT();
  const { data, isLoading, error, refetch, isFetching } = useUsers();
  const all = useMemo(() => data?.data ?? [], [data]);
  const { map: tenants } = useTenantMap();

  const [view, setView] = usePersistedState<ViewMode>('admin.users.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.users.pageSize', 25);

  const [query, setQuery] = useState('');
  const [kpi, setKpi] = useState<KpiKey | null>('all');
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);

  const debouncedQuery = useDebounce(query, 250);

  const recentCutoff = useMemo(() => Date.now() - 30 * 86_400_000, []);
  const counts = useMemo(() => ({
    all: all.length,
    tenant: all.filter((u) => u.scope === 'tenant').length,
    platform: all.filter((u) => u.scope === 'platform').length,
    recent: all.filter((u) => new Date(u.created_at).getTime() >= recentCutoff).length
  }), [all, recentCutoff]);

  const kpiItems: KpiFilterItem[] = [
    { id: 'all',      label: t('users.kpi.all'),      value: counts.all,      tone: 'primary' },
    { id: 'tenant',   label: t('users.kpi.tenant'),   value: counts.tenant,   tone: 'pending' },
    { id: 'platform', label: t('users.kpi.platform'), value: counts.platform, tone: 'warning' },
    { id: 'recent',   label: t('users.kpi.recent'),   value: counts.recent,   tone: 'healthy' }
  ];

  const filterFields: FilterField[] = useMemo(() => {
    // The drawer used to offer "3f8a1c2b…" as an organisation. Name the
    // organisations, and sort by that name so the list reads alphabetically.
    const orgs = Array.from(new Set(all.map((u) => u.organisation_id)));
    const options = orgs
      .map((id) => ({ value: id, label: tenants.get(id)?.name ?? t('tenantName.unknownShort') }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [
      {
        key: 'scope',
        label: t('users.filters.scope'),
        placeholder: t('users.filters.anyScope'),
        options: [
          { value: 'tenant', label: t('users.scope.tenant') },
          { value: 'platform', label: t('users.scope.platform') }
        ]
      },
      {
        key: 'organisation',
        label: t('users.filters.organisation'),
        placeholder: t('users.filters.anyOrganisation'),
        options
      }
    ];
  }, [all, tenants, t]);

  const drawerActive = Object.values(filters).filter((v) => v && v !== 'all').length;
  const hasActiveFilters =
    Boolean(query.trim()) || (kpi !== null && kpi !== 'all') || drawerActive > 0;

  function clearAllFilters() {
    setQuery('');
    setKpi('all');
    setFilters({});
    setPage(1);
  }

  const kpiLabels: Record<KpiKey, string> = {
    all: t('users.kpi.all'),
    tenant: t('users.kpi.tenant'),
    platform: t('users.kpi.platform'),
    recent: t('users.kpi.recent')
  };

  // What is narrowing the list, one removable pill each. The count badge on
  // the Filters button says how many are on but never which.
  const pills: ActiveFilterPill[] = [];
  if (kpi && kpi !== 'all') pills.push({ id: 'kpi', field: t('filters.field.show'), value: kpiLabels[kpi] });
  if (query.trim()) pills.push({ id: 'search', field: t('common.search'), value: query.trim() });
  for (const field of filterFields) {
    const value = filters[field.key];
    if (!value) continue;
    const option = field.options.find((o) => o.value === value);
    pills.push({ id: field.key, field: field.label, value: option?.label ?? value });
  }

  function removePill(id: string) {
    setPage(1);
    if (id === 'kpi') { setKpi('all'); return; }
    if (id === 'search') { setQuery(''); return; }
    setFilters((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
  }

  const pillRow = <ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />;

  const filtered = useMemo(() => {
    let rows = all;
    if (kpi && kpi !== 'all') {
      rows = rows.filter((u) => {
        switch (kpi) {
          case 'tenant':   return u.scope === 'tenant';
          case 'platform': return u.scope === 'platform';
          case 'recent':   return new Date(u.created_at).getTime() >= recentCutoff;
          default:         return true;
        }
      });
    }
    if (filters.scope === 'tenant') rows = rows.filter((u) => u.scope === 'tenant');
    if (filters.scope === 'platform') rows = rows.filter((u) => u.scope === 'platform');
    if (filters.organisation) rows = rows.filter((u) => u.organisation_id === filters.organisation);

    const q = debouncedQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (u) =>
          // `email` is nullable in the database though the SDK types it as a
          // string; an unguarded toLowerCase() here blanked the whole page.
          (u.email ?? '').toLowerCase().includes(q) ||
          (u.display_name ?? '').toLowerCase().includes(q) ||
          (tenants.get(u.organisation_id)?.name ?? '').toLowerCase().includes(q) ||
          u.organisation_id.toLowerCase().includes(q) ||
          u.id.toLowerCase().includes(q)
      );
    }
    return rows;
  }, [all, kpi, filters, debouncedQuery, recentCutoff, tenants]);

  useEffect(() => {
    if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1);
  }, [filtered.length, page, pageSize]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const platformIsEmpty = !error && all.length === 0;
  // A silent truncation makes every count on this page a lie, including the
  // KPI row, so it is stated rather than implied. Since 5 September the
  // endpoint reports the real total for the filter, so the notice can say how
  // much is behind the page instead of only that something is.
  const window = data?.meta;
  const truncated = window ? window.capped : all.length >= SERVER_ROW_CAP;

  return (
    <Stack gap="lg">
      <PageHeader title={t('users.title')} description={t('users.description')} />
      {truncated && (
        <Alert color="warning">
          {window
            ? t('users.truncatedTotal', { returned: window.returned, total: window.total })
            : t('users.truncated', { cap: SERVER_ROW_CAP })}
        </Alert>
      )}
      <TableShell
        title={t('users.tableTitle')}
        kpis={
          <KpiFilterRow
            items={kpiItems}
            value={kpi}
            onChange={(next) => { setKpi((next ?? 'all') as KpiKey); setPage(1); }}
          />
        }
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('users.searchPlaceholder')}
        filters={
          <Group gap="sm" wrap="nowrap">
            <FilterDrawer
              triggerLabel={t('common.filters')}
              title={t('filters.drawer.title')}
              description={t('filters.drawer.description')}
              labels={{ apply: t('action.apply'), cancel: t('action.cancel'), clear: t('filters.clearAll'), any: t('filters.any') }}
              fields={filterFields}
              values={filters}
              onApply={(next) => { setFilters(next); setPage(1); }}
            />
          </Group>
        }
        view={view}
        onViewChange={setView}
        loading={isLoading}
        isEmpty={filtered.length === 0}
        empty={
          <Stack gap="md" align="center">
            {pillRow}
            <EmptyState
              title={platformIsEmpty ? t('users.emptyPlatform') : t('users.empty')}
              description={platformIsEmpty ? t('users.emptyPlatformHint') : t('users.emptyHint')}
              action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined}
            />
          </Stack>
        }
        error={
          error ? (
            <Alert color="error">
              <Stack gap="sm">
                <span>{t('users.loadError')}</span>
                <Group gap="sm">
                  <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>
                    {t('action.retry')}
                  </Button>
                </Group>
              </Stack>
            </Alert>
          ) : null
        }
      >
        {/* The pills and the footer sit inside the body rather than in the
            shell's own slots, because the design puts them hard against the
            table: the pills under the toolbar, the range and pager under the
            last row. */}
        <Stack gap="md">
          {pillRow}
          {view === 'table' && <UsersTable records={paged} loading={isLoading} />}
          {view === 'cards' && <UsersGrid records={paged} />}
          {view === 'list'  && <UsersList records={paged} />}
          <TableFooter
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            capped={truncated}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        </Stack>
      </TableShell>
    </Stack>
  );
}
